/*
 * Copyright (c) 2017-2019 by Gary Kumfert. All rights reserved.
 *
 * MIT License
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software
 * and associated documentation files (the "Software"), to deal in the Software without restriction,
 * including without limitation the rights to use, copy, modify, merge, publish, distribute,
 * sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or
 * substantial portions of the Software.
 */
import {
    Directive,
    Injectable,
    Input,
    Output,
    EventEmitter,
    ElementRef,
    ChangeDetectorRef,
    HostListener,
} from '@angular/core';

import { NGXLogger } from 'ngx-logger';

import { DNDItemDirective } from './dnd.item.directive';
import { DNDInfo, DNDService } from './dnd.service';

@Directive({
    selector: '[dnd-container]',
})
export class DNDContainerDirective {

    /**
     * Allows drop on this element
     */
    @Input()
    dropEnabled: boolean = true;

    /**
     * A reference to the underlying model, if needed.
     */
    @Input()
    dropData: any;

    /**
     * foo
     */
    @Input()
    copyNotMove: boolean = false;

    @Input()
    deleteOnDragOff: boolean = false;

    @Input()
    sortableData: any[] = [];

    dnditems: DNDItemDirective[] = [];

    @Input()
    allowedTypes: string[] = [];

    // TODO: used to grant finer control than dropZones
    @Input()
    allowDrop: Function; // (dropData:any, dragTypes:Array<any>):boolean

    // used to promote types if needed when dragging into new containers.
    @Input()
    dataConvert: Function; // (dropData:any, dragTypes:Array<any>):any

    @Output() onDragEnter: EventEmitter<DNDInfo> = new EventEmitter<DNDInfo>();
    @Output() onDragOver: EventEmitter<DNDInfo> = new EventEmitter<DNDInfo>();
    @Output() onDragLeave: EventEmitter<DNDInfo> = new EventEmitter<DNDInfo>();
    @Output() onDragDrop: EventEmitter<DNDInfo> = new EventEmitter<DNDInfo>();

    // higher level events
    // fires only on drag-to-delete
    @Output() onDragDelete: EventEmitter<DNDInfo> = new EventEmitter<DNDInfo>();  // TODO: this doesn't work, use onDragLeave instead?
    // fires only after a drop completes on a new target
    @Output() onDropSuccess: EventEmitter<DNDInfo> = new EventEmitter<DNDInfo>();
    // fires only after an item has been reordered in the same target
    @Output() onSortSuccess: EventEmitter<DNDInfo> = new EventEmitter<DNDInfo>();

    get isSortable(): boolean {
        return !!this.sortableData;
    }

    public get items(): DNDItemDirective[] {
        return this.dnditems;
    }

    constructor(
        public el: ElementRef,
        // private renderer: Renderer2,
        private dnd: DNDService,
        private changeDetector: ChangeDetectorRef,
        private logger: NGXLogger,
    ) {
    }

    @HostListener('dragenter', ['$event']) _onDragEnter(event: any): void {
        if (!this.dnd.isDropAllowed(event, this)) { return; }
        // this.renderer.addClass(this.el.nativeElement, 'dnd-drop-continue');
        let dndInfo: DNDInfo = this.dnd.onDragEnter(event, this);
        this.onDragEnter.emit(dndInfo);
        // this.logger.log('dnd.container: onDragEnter ', event);
    }

    @HostListener('dragover', ['$event']) _onDragOver(event: any): void {
        if (!this.dnd.isDropAllowed(event, this)) { return; }
        this._preventDefault(event);
        // this.renderer.addClass(this.el.nativeElement, 'dnd-drop-continue');
        let dndInfo: DNDInfo = this.dnd.onDragOver(event, this);
        this.onDragOver.emit(dndInfo);
        // this.logger.log('dnd.container: onDragOver ', event);
    }

    @HostListener('dragleave', ['$event']) _onDragLeave(event: any): void {
        if (!this.dnd.isDropAllowed(event, this)) { return; }
        // this.renderer.removeClass(this.el.nativeElement, 'dnd-drop-continue');
        let dndInfo: DNDInfo = this.dnd.onDragLeave(event, this);
        this.onDragLeave.emit(dndInfo);
        // this.logger.log('dnd.container: onDragLeave ', event);
    }

    @HostListener('drop', ['$event']) _onDragDrop(event: any): void {
        if (!this.dnd.isDropAllowed(event, this)) { return; }
        this._preventDefault(event);

        // this.renderer.removeClass(this.el.nativeElement, 'dnd-drop-continue');
        let dndInfo: DNDInfo = this.dnd.onDragDrop(event, this);
        this.onDragDrop.emit(dndInfo);
        // var data = event.dataTransfer.getData('any');
        // this.logger.log('dnd.container: onDragDrop ', event);
        // this.logger.log('dnd.container: el = ', this.el);
        // this.logger.log('dnd.container: data = ', event.dataTransfer.getData('any'));
        // this.logger.log('dnd.container: DOM obj ', document.getElementById('droppable1'));
    }

    /*
	 * In some cases, default behavior of a browser actually gets in the way of drag-n-drop
	 * behavior.  So we prevent this default behavior in certain circumstances.
	 *
	 * Examples:
	 *    1.  onDragOver() by default, data/elements cannot be dragged over others, so this overrides that.
 	 * 	 2. onDragDrop() by default, drops are interpreted by the browser to open links.
	 */
    private _preventDefault(event: any): void {
        if (event.preventDefault) { event.preventDefault(); }
    }

    /******* Change detection ******/
    private detectChanges(): void {
        // Programmatically run change detection to fix issue in Safari
        setTimeout(() => {
            this.changeDetector.detectChanges();
        }, 250);
    }
}
